/** The native video properties needed to wait for a new, decodable camera frame. */
type FrameSurface = Pick<
  HTMLVideoElement,
  "readyState" | "paused" | "ended" | "videoWidth" | "videoHeight" | "currentTime"
> & {
  requestVideoFrameCallback?: HTMLVideoElement["requestVideoFrameCallback"];
  cancelVideoFrameCallback?: HTMLVideoElement["cancelVideoFrameCallback"];
  getVideoPlaybackQuality?: HTMLVideoElement["getVideoPlaybackQuality"];
};

/**
 * A demand-driven frame boundary, not a frame queue. The detector calls `next` only
 * after its preceding run finishes, so frames that arrived during inference are skipped.
 * Cancel releases a pending wait (for a muted track); stop also prevents future waits.
 */
export class VideoFrames {
  private stopped = false;
  private cancelPending: (() => void) | null = null;
  private lastMediaTime: number | null = null;
  private lastPresentedFrames: number | null = null;
  private polling = false;
  private readonly video: FrameSurface;
  private readonly fallbackIntervalMs: number;

  constructor(video: FrameSurface, fallbackIntervalMs: number) {
    this.video = video;
    this.fallbackIntervalMs = fallbackIntervalMs;
  }

  next(): Promise<boolean> {
    this.cancel();
    if (this.stopped) return Promise.resolve(false);

    const video = this.video;
    const useVideoCallback =
      !this.polling &&
      typeof video.requestVideoFrameCallback === "function" &&
      typeof video.cancelVideoFrameCallback === "function";
    // Establish the fallback boundary now, not at the previous detector start. A frame
    // retained during a pause or delivered during inference is not a fresh next frame.
    const boundaryTime = video.currentTime;
    const decodedFrames = (): number | null => {
      const count = video.getVideoPlaybackQuality?.().totalVideoFrames;
      return count !== undefined && Number.isFinite(count) && count > 0 ? count : null;
    };
    const boundaryFrames = decodedFrames();

    return new Promise<boolean>((resolve) => {
      let active = true;
      let frameId: number | null = null;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const finish = (fresh: boolean): void => {
        if (!active) return;
        active = false;
        if (frameId !== null) video.cancelVideoFrameCallback?.(frameId);
        if (timer !== null) clearTimeout(timer);
        this.cancelPending = null;
        resolve(fresh);
      };
      this.cancelPending = () => finish(false);

      const decodable = (): boolean =>
        video.readyState >= 2 &&
        !video.paused &&
        !video.ended &&
        video.videoWidth > 0 &&
        video.videoHeight > 0;

      const onFrame: VideoFrameRequestCallback = (_now, metadata) => {
        if (!active) return;
        frameId = null;
        const mediaTime = Number.isFinite(metadata.mediaTime) ? metadata.mediaTime : null;
        const presented = Number.isFinite(metadata.presentedFrames)
          ? metadata.presentedFrames
          : null;
        // Live WebKit streams can repeat mediaTime while presentedFrames advances.
        // Either advancing counter proves a new frame; neither is authoritative alone.
        const fresh =
          (mediaTime !== null && mediaTime !== this.lastMediaTime) ||
          (presented !== null && presented !== this.lastPresentedFrames);
        this.lastMediaTime = mediaTime;
        this.lastPresentedFrames = presented;
        if (decodable() && fresh) {
          finish(true);
        } else {
          frameId = video.requestVideoFrameCallback!(onFrame);
        }
      };

      const poll = (): void => {
        if (!active) return;
        timer = null;
        const count = decodedFrames();
        const fresh =
          boundaryFrames !== null && count !== null
            ? count !== boundaryFrames
            : video.currentTime !== boundaryTime;
        if (decodable() && fresh) {
          // The compositor callback stalled but the decoder advanced. Stay on polling
          // for this camera lifetime instead of paying the watchdog delay on every frame.
          this.polling = true;
          finish(true);
        } else timer = setTimeout(poll, this.fallbackIntervalMs);
      };

      if (useVideoCallback) {
        frameId = video.requestVideoFrameCallback!(onFrame);
        // Native callbacks normally cancel this before it fires. A supported but stalled
        // callback API must not leave the scanner waiting forever beside a moving preview.
        timer = setTimeout(poll, Math.max(250, this.fallbackIntervalMs * 3));
      } else {
        timer = setTimeout(poll, this.fallbackIntervalMs);
      }
    });
  }

  cancel(): void {
    this.cancelPending?.();
  }

  stop(): void {
    this.stopped = true;
    this.cancel();
  }
}
